import { useState } from "react";

/**
 * State machine behind the shared repeatable child-list editor used by §13
 * episodic waste lines, §14 consolidated sites, and §16 HSM activities on the
 * Filing Notifications step. Kept in its own (non-component) module so the
 * `subRecordListEditor` component file can stay Fast Refresh-friendly.
 *
 * The dialog (see `SubRecordDialog`) edits an ISOLATED draft form and only calls
 * `commit` on Save, so the parent form is never touched — and no validation
 * runs — until then.
 */

export type SubRecordRow<T> = T & { id: number };

type OpenState = { mode: "add" } | { mode: "edit"; index: number };

/**
 * Owns the open/commit state, the delete-confirm state, and the derived grid
 * rows for a field-array-backed child list. Callers wire in their
 * `useFieldArray` operations (kept typed at the call site) plus a `getItem`
 * reader.
 */
export function useSubRecordEditor<T extends object>({
  items,
  append,
  update,
  remove,
  getItem,
}: {
  items: T[];
  append: (value: T) => void;
  update: (index: number, value: T) => void;
  remove: (index: number) => void;
  getItem: (index: number) => T;
}) {
  const [open, setOpen] = useState<OpenState | null>(null);
  // The row pending delete confirmation (captured at click time).
  const [rowToDelete, setRowToDelete] = useState<SubRecordRow<T> | null>(null);

  const openAdd = () => setOpen({ mode: "add" });
  const openEdit = (index: number) => setOpen({ mode: "edit", index });
  const close = () => setOpen(null);

  // The current values for the open dialog: the existing row when editing, or
  // null when adding (the caller seeds an empty draft).
  const editingValues: T | null = open?.mode === "edit" ? getItem(open.index) : null;

  const commit = (values: T) => {
    if (open?.mode === "edit") {
      update(open.index, values);
    } else {
      append(values);
    }
    setOpen(null);
  };

  const confirmDelete = () => {
    if (!rowToDelete) {
      return;
    }
    remove(rowToDelete.id);
    setRowToDelete(null);
  };

  const rows: SubRecordRow<T>[] = items.map((item, index) => ({ ...item, id: index }));

  return {
    isOpen: open !== null,
    isAdding: open?.mode === "add",
    editingValues,
    rows,
    openAdd,
    openEdit,
    close,
    commit,
    rowToDelete,
    requestDelete: setRowToDelete,
    confirmDelete,
  };
}
